import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, deleteToken } from 'firebase/messaging';


const stripQuotes = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(/^["']|["']$/g, '');
};

const firebaseConfig = {
  apiKey: stripQuotes(process.env.REACT_APP_FIREBASE_API_KEY),
  authDomain: stripQuotes(process.env.REACT_APP_FIREBASE_AUTH_DOMAIN),
  projectId: stripQuotes(process.env.REACT_APP_FIREBASE_PROJECT_ID),
  storageBucket: stripQuotes(process.env.REACT_APP_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: stripQuotes(process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID),
  appId: stripQuotes(process.env.REACT_APP_FIREBASE_APP_ID),
};

const VAPID_KEY = stripQuotes(process.env.REACT_APP_FIREBASE_VAPID_KEY);

if (!VAPID_KEY) {
  console.warn('[Firebase] VAPID_KEY is missing! Add REACT_APP_FIREBASE_VAPID_KEY to your .env file');
  console.warn('[Firebase] Get it from: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates');
}

let app = null;
let messaging = null;

try {
  if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
  } else {
    console.warn('[Firebase] Configuration missing. Push notifications will not work.');
  }
} catch (error) {
  console.error('[Firebase] Initialization error:', error);
}

const waitForServiceWorkerActive = (registration) => {
  if (registration.active) {
    return Promise.resolve(registration);
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) {
    return navigator.serviceWorker.ready.then(() => registration);
  }

  return new Promise((resolve, reject) => {
    const onStateChange = () => {
      if (worker.state === 'activated') {
        worker.removeEventListener('statechange', onStateChange);
        resolve(registration);
      } else if (worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        reject(new Error('Service worker became redundant before activation'));
      }
    };
    worker.addEventListener('statechange', onStateChange);
    if (worker.state === 'activated') {
      onStateChange();
    }
  });
};

const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });
    // getToken's PushManager.subscribe requires an *active* worker — register()
    // alone can resolve while the SW is still installing.
    await waitForServiceWorkerActive(registration);
    await navigator.serviceWorker.ready;
    if (!registration.active) {
      throw new Error('Service worker registered but not active');
    }
    return registration;
  } catch (error) {
    console.error('[FCM] Service worker registration failed:', error);
    throw error;
  }
};

const clearBrokenPushSubscription = async (registration) => {
  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      console.warn('[FCM] Cleared broken push subscription');
    }
  } catch (error) {
    console.warn('[FCM] Could not clear push subscription:', error.message);
  }

  try {
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (error) {
    // No existing IndexedDB token is fine.
  }
};

const requestToken = async (serviceWorkerRegistration) =>
  getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration,
  });


export const getFCMToken = async () => {
  try {
    if (!messaging) {
      console.warn('[FCM] Messaging not initialized. Check Firebase configuration.');
      return null;
    }

    if (!VAPID_KEY) {
      console.warn('[FCM] VAPID key not configured. Check REACT_APP_FIREBASE_VAPID_KEY.');
      return null;
    }
    const permission = await Notification.requestPermission();
    
    if (permission !== 'granted') {
      return null;
    }

    const serviceWorkerRegistration = await registerServiceWorker();

    try {
      const token = await requestToken(serviceWorkerRegistration);
      return token || null;
    } catch (error) {
      const message = error?.message || '';
      // Chrome often leaves a wedged PushSubscription after failed attempts /
      // TOO_MANY_REGISTRATIONS noise. Clear once and retry.
      if (
        error?.name === 'AbortError' ||
        message.includes('push service error') ||
        message.includes('no active Service Worker')
      ) {
        console.warn('[FCM] Push subscribe failed — clearing subscription and retrying once...');
        await clearBrokenPushSubscription(serviceWorkerRegistration);
        await new Promise((resolve) => setTimeout(resolve, 300));
        const token = await requestToken(serviceWorkerRegistration);
        return token || null;
      }
      throw error;
    }
  } catch (error) {
    console.error('[FCM] Error getting token:', error);
    return null;
  }
};


export const onForegroundMessage = (callback) => {
  if (!messaging) {
    console.warn('[FCM] Cannot subscribe to messages. Messaging not initialized.');
    return () => {};
  }

  const unsubscribe = onMessage(messaging, (payload) => {
    if (callback && typeof callback === 'function') {
      callback(payload);
    }
  });

  return unsubscribe;
};

export const isPushNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

export { messaging };
